import { useMutation, useQuery } from '@apollo/client'
import {
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { SendIcon } from 'lucide-react'
import { useState } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_TENANT_INVITATIONS,
  GetTenantInvitationsQuery,
  GetTenantInvitationsQueryVariables,
  InvitationOrder,
  RESEND_INVITATION,
  SortDirection,
} from '@/graphql'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import useT from '@/i18n/useT'
import { LocalizationHelpers } from '@/utils/localization.helpers'

import DeliciousTable from '../components/DeliciousTable'
import CreateUserForm from '../forms/user/CreateUserForm'
import ImportUsersCsvDialog from '../forms/user/ImportUsersCsvDialog'
import InviteUserForm from '../forms/user/InviteUserForm'
import { Chip } from './UserList'

type InviteRow = {
  id: string
  email: string
  opened: boolean
  groups: string
}

const renderInviteRow = (
  user: InviteRow,
  index: number,
  t: (key: string, params?: Record<string, any>) => string,
  onResend: (invitationId: string) => void,
) => (
  <TableRow hover role="checkbox" tabIndex={-1} key={`${user.id}-${index}`}>
    <TableCell align="left">{user.email}</TableCell>
    <TableCell align="left">{user.groups}</TableCell>
    <TableCell>
      <Chip
        size="small"
        mr={1}
        mb={1}
        label={
          user.opened
            ? t('inviteUserPage.status.opened')
            : t('inviteUserPage.status.notOpened')
        }
        status={user.opened ? 'active' : 'inactive'}
      />
    </TableCell>
    <TableCell>
      <Button
        size="small"
        onClick={() => onResend(user.id)}
        disabled={user.opened}
      >
        {t('inviteUserPage.actions.resend')}
      </Button>
    </TableCell>
  </TableRow>
)

const InviteUserPage = () => {
  const { t } = useT()

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [importUsersDialogOpen, setImportUsersDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { order, orderBy, handleRequestSort } = useTableOrder<InvitationOrder>(
    'email',
    SortDirection.ASC,
  )

  const onCancel = () => {
    setInviteDialogOpen(false)
  }

  const invitation = useQuery<
    GetTenantInvitationsQuery,
    GetTenantInvitationsQueryVariables
  >(GET_TENANT_INVITATIONS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
      filters: {
        email: { iContains: searchTerm },
        invalidated: { exact: false },
      },
      order: { [orderBy]: order },
    },
  })

  const [resendInvitation] = useMutation(RESEND_INVITATION, {
    onCompleted: () => {
      invitation.refetch()
    },
    onError: error => {
      console.error('Error resending invitation:', error)
    },
  })

  useRefresh(invitation.refetch)

  const onInviteSuccess = () => {
    setInviteDialogOpen(false)
    invitation.refetch()
  }

  const handleResendInvitation = (invitationId: string) => {
    resendInvitation({
      variables: { invitationId },
    })
  }

  const invites = invitation?.data?.tenantInvitations.results || []
  const mappedInvites = invites.map(invite => ({
    id: invite.id,
    email: invite.email,
    opened: invite.opened,
    groups: invite.groups
      .map(group => LocalizationHelpers.localizedGroupName(t, group.name))
      .join(', '),
  }))

  return (
    <>
      <Stack spacing={3} pb={3} pt={6}>
        <Stack direction="row" spacing={3}>
          <TextField
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            label={t('inviteUserPage.search')}
            placeholder={t('inviteUserPage.searchPlaceholder')}
            variant="outlined"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Button
            startIcon={<SendIcon />}
            variant="contained"
            onClick={() => {
              setShowCreateUser(false)
              setInviteDialogOpen(true)
            }}
          >
            {t('inviteUserPage.actions.invite')}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              setShowCreateUser(true)
              setInviteDialogOpen(true)
            }}
          >
            {t('inviteUserPage.actions.createUser')}
          </Button>
          <Tooltip
            title={t(
              'inviteUserPage.actions.importUsersCsv',
              'Import Users from CSV',
            )}
          >
            <Button
              variant="contained"
              onClick={() => setImportUsersDialogOpen(true)}
              aria-label={t(
                'inviteUserPage.actions.importUsersCsv',
                'Import Users from CSV',
              )}
            >
              <UploadFileIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
        <DeliciousTable<InviteRow, InvitationOrder>
          title={t('inviteUserPage.table.title')}
          data={mappedInvites}
          order={order === SortDirection.ASC ? 'asc' : 'desc'}
          handleRequestSort={handleRequestSort}
          orderBy={orderBy}
          headCells={[
            {
              id: 'email',
              alignment: 'left',
              label: t('inviteUserPage.table.email'),
              sortKey: 'email',
            },
            {
              id: 'groups',
              alignment: 'left',
              label: t('inviteUserPage.table.role'),
            },
            {
              id: 'opened',
              alignment: 'left',
              label: t('inviteUserPage.table.status'),
              sortKey: 'opened',
            },
            {
              id: 'actions',
              alignment: 'left',
              label: t('inviteUserPage.table.actions'),
            },
          ]}
          renderRow={(user, index) =>
            renderInviteRow(user, index, t, handleResendInvitation)
          }
          loading={invitation.loading}
          error={invitation.error?.message}
          totalCount={invitation?.data?.tenantInvitations.totalCount || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </Stack>
      <Dialog
        fullWidth
        open={inviteDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            return
          }
          setInviteDialogOpen(false)
        }}
        disableEscapeKeyDown
      >
        <DialogContent>
          {!showCreateUser && (
            <>
              <Typography variant="h3" sx={{ pb: 3 }}>
                {t('inviteUser.title')}
              </Typography>
              <InviteUserForm onSuccess={onInviteSuccess} onCancel={onCancel} />
            </>
          )}
          {showCreateUser && <CreateUserForm onCancel={onCancel} />}
        </DialogContent>
      </Dialog>
      <ImportUsersCsvDialog
        open={importUsersDialogOpen}
        onClose={() => setImportUsersDialogOpen(false)}
        onSuccess={() => {
          setImportUsersDialogOpen(false)
          invitation.refetch()
        }}
      />
    </>
  )
}

export default InviteUserPage
